// ─── BS Analysis Panel — extracted from AdminCCFSLeads.jsx (Phase 2a) ───
// State-owning component handling BS/P&L editing, AOC-4 parsing/locking and YoY comparison.
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Loader2, CheckCircle2, Lock, Unlock, Save, Flag, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { DiscoveryFlagToggle } from '@/pages/admin/ccfs/components/DiscoveryFlagToggle';
import { flagRowClass } from '@/pages/admin/ccfs/utils';
import { BS_LINE_ITEMS, PL_LINE_ITEMS, CLOSURE_PATHS, COMPLEXITY_LEVELS } from '@/pages/admin/ccfs/constants';

const API = process.env.REACT_APP_BACKEND_URL;

export function BSAnalysisPanel({ lead, onSave, onRefresh, readOnly = false }) {
  const stored = lead.bs_analysis || {};
  const parsed = lead.aoc4_parsed || {};
  const isLocked = !!stored.locked;
  // BS/P&L input fields are disabled when parent says readOnly OR when BS section is locked
  const fieldsReadOnly = readOnly || isLocked;

  // Initialize items by merging stored bs_analysis with aoc4_parsed filings_to_check
  const initializeItems = () => {
    const storedItems = stored.items || {};
    const parsedItems = parsed.bs_items || {};
    const merged = { ...storedItems };

    // Merge filings_to_check from aoc4_parsed if not already in stored items
    for (const [key, val] of Object.entries(parsedItems)) {
      if (!merged[key]) {
        merged[key] = {};
      }
      // Always use filings_to_check from parsed data if available
      if (val.filings_to_check) {
        merged[key].filings_to_check = val.filings_to_check;
      }
      // Use amount from parsed if stored doesn't have it
      if (val.amount && !merged[key].amount) {
        merged[key].amount = val.amount;
      }
    }
    return merged;
  };

  const [items, setItems] = useState(initializeItems);
  const [plItems, setPlItems] = useState(() => {
    const storedPl = stored.pl_items || {};
    const parsedPl = parsed.pl_summary || {};
    const merged = { ...storedPl };
    PL_LINE_ITEMS.forEach(li => {
      if (!merged[li.key]) merged[li.key] = {};
      if (!merged[li.key].filings_to_check) merged[li.key].filings_to_check = li.filings || '';
      if (!merged[li.key].amount && parsedPl[li.key]) merged[li.key].amount = String(parsedPl[li.key]);
    });
    return merged;
  });
  // Previous-year values from parsed AOC-4 (display-only for YoY comparison)
  const parsedBsPrev = Object.fromEntries(
    Object.entries(parsed.bs_items || {}).map(([k, v]) => [k, v?.amount_prev || ''])
  );
  const parsedPlPrev = parsed.pl_summary_prev || {};
  const periodPrevious = parsed.aoc4_source?.period_to_prev || '';

  // ─── P&L total auto-compute ───────────────────────────────────────────────
  // Totals are derived from editable line items unless the admin has explicitly
  // overridden a total cell (stored as plItems[key].manual_override = true).
  // Net Profit = PBT by default; admin can override to factor tax expense.
  useEffect(() => {
    const num = (k) => parseFloat(plItems?.[k]?.amount) || 0;
    const totalRevenue  = num('revenue_operations') + num('other_income');
    const totalExpenses = ['cost_materials','purchases_stock','employee_expense','finance_cost','depreciation','other_expenses']
      .reduce((s, k) => s + num(k), 0);
    const pbt    = totalRevenue - totalExpenses;
    const netPl  = pbt; // Net = PBT (no separate tax row; admin can override)
    const fmt = (n) => (totalRevenue || totalExpenses) ? String(n) : '';
    setPlItems(prev => {
      const out = { ...prev };
      let changed = false;
      const apply = (key, computed) => {
        const cur = out[key] || {};
        if (cur.manual_override) return; // admin override — leave alone
        const next = fmt(computed);
        if ((cur.amount || '') !== next) {
          out[key] = { ...cur, amount: next };
          changed = true;
        }
      };
      apply('total_revenue',     totalRevenue);
      apply('total_expenses',    totalExpenses);
      apply('profit_before_tax', pbt);
      apply('net_profit_loss',   netPl);
      return changed ? out : prev;
    });
    // Only recompute when an editable line item changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    plItems?.revenue_operations?.amount,
    plItems?.other_income?.amount,
    plItems?.cost_materials?.amount,
    plItems?.purchases_stock?.amount,
    plItems?.employee_expense?.amount,
    plItems?.finance_cost?.amount,
    plItems?.depreciation?.amount,
    plItems?.other_expenses?.amount,
    plItems?.total_revenue?.manual_override,
    plItems?.total_expenses?.manual_override,
    plItems?.profit_before_tax?.manual_override,
    plItems?.net_profit_loss?.manual_override,
  ]);

  // Tiny inline YoY delta badge
  const YoYCell = ({ prevValue, currentValue }) => {
    const prev = parseFloat(prevValue);
    if (!prevValue || isNaN(prev) || prev === 0) return null;
    const curr = parseFloat(currentValue) || 0;
    const delta = curr - prev;
    const pct = prev !== 0 ? Math.round((delta / Math.abs(prev)) * 100) : 0;
    const significant = Math.abs(pct) >= 10;
    const isUp = delta > 0;
    return (
      <span
        className={`text-[8px] tabular-nums text-right mt-0.5 flex items-center justify-end gap-0.5 ${
          significant ? (isUp ? 'text-emerald-600' : 'text-red-500') : 'text-slate-400'
        }`}
        title={`Previous FY (${periodPrevious || 'PY'}): ₹${prev.toLocaleString('en-IN')}${pct !== 0 ? ` • Δ ${isUp ? '+' : ''}${pct}%` : ''}`}
      >
        PY: ₹{prev.toLocaleString('en-IN')}
        {significant && <span>{isUp ? '▲' : '▼'}{Math.abs(pct)}%</span>}
      </span>
    );
  };
  const [overallNotes, setOverallNotes] = useState(stored.overall_notes || '');
  const [soaComplexity, setSoaComplexity] = useState(stored.soa_complexity || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [locking, setLocking] = useState(false);
  const [dirty, setDirty] = useState(false);
  const aoc4Ref = useRef(null);

  const handleAoc4Lock = async () => {
    if (!window.confirm('Lock AOC-4 parsed data? Re-upload will be blocked until you unlock. Verify that the BS & P&L values displayed match the uploaded PDF before locking.')) return;
    setLocking(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/aoc4-lock`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ locked: true }),
      });
      if (!res.ok) throw new Error('lock failed');
      toast.success('AOC-4 locked');
      onRefresh?.();
    } catch { toast.error('Failed to lock AOC-4'); }
    finally { setLocking(false); }
  };

  const handleAoc4Unlock = async () => {
    if (!window.confirm('Unlock AOC-4? This will allow re-uploading and may change existing BS/P&L values.')) return;
    setLocking(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/aoc4-lock`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ locked: false }),
      });
      if (!res.ok) throw new Error('unlock failed');
      toast.success('AOC-4 unlocked');
      onRefresh?.();
    } catch { toast.error('Failed to unlock AOC-4'); }
    finally { setLocking(false); }
  };

  const handleBsUnlock = async () => {
    if (!window.confirm('Unlock BS Analysis? This will allow editing of all P&L and BS items. Changes will be re-locked on next save.')) return;
    setLocking(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/bs-analysis-lock`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ locked: false }),
      });
      if (!res.ok) throw new Error('unlock failed');
      toast.success('BS Analysis unlocked');
      onRefresh?.();
    } catch { toast.error('Failed to unlock BS Analysis'); }
    finally { setLocking(false); }
  };

  const updateItem = (key, field, value) => {
    setItems(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value }
    }));
    setDirty(true);
  };

  const updatePlItem = (key, field, value) => {
    setPlItems(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value }
    }));
    setDirty(true);
  };

  const handleAoc4Upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/aoc4-parse`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        // Surface non-fatal parser warnings (e.g., BS-date mismatch, empty P&L) — each as its own toast
        if (Array.isArray(data.warnings)) {
          for (const w of data.warnings) {
            toast.warning(w, { duration: 8000 });
          }
        }
        // Auto-populate BS items from parsed data
        const parsedItems = data.data?.bs_items || {};
        setItems(prev => {
          const merged = { ...prev };
          for (const [key, val] of Object.entries(parsedItems)) {
            merged[key] = {
              ...(merged[key] || {}),
              amount: val.amount || merged[key]?.amount || '',
              filings_to_check: val.filings_to_check || '',
              // Keep existing closure_path, complexity, notes if already set
              closure_path: merged[key]?.closure_path || '',
              complexity: merged[key]?.complexity || '',
              notes: merged[key]?.notes || '',
            };
          }
          return merged;
        });
        // Auto-populate P&L items
        const parsedPl = data.data?.pl_summary || {};
        setPlItems(prev => {
          const merged = { ...prev };
          PL_LINE_ITEMS.forEach(li => {
            merged[li.key] = {
              ...(merged[li.key] || {}),
              amount: parsedPl[li.key] ? String(parsedPl[li.key]) : merged[li.key]?.amount || '',
              filings_to_check: li.filings || '',
              closure_path: merged[li.key]?.closure_path || '',
              complexity: merged[li.key]?.complexity || '',
              notes: merged[li.key]?.notes || '',
            };
          });
          return merged;
        });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to parse AOC-4');
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave({ items, pl_items: plItems, overall_notes: overallNotes, soa_complexity: soaComplexity });
    setDirty(false);
    setSaving(false);
  };

  const filledCount = BS_LINE_ITEMS.filter(li => items[li.key]?.closure_path && items[li.key].closure_path !== '').length;

  return (
    <div className="space-y-3">
      {/* AOC-4 Upload + Lock */}
      {!readOnly && (
      <div className="flex items-center gap-3 pb-2 border-b border-slate-100 flex-wrap">
        {/* BS date info prompt */}
        {lead.mca_company_info?.date_of_balance_sheet && (
          <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 shrink-0">
            Last filed BS: <strong>{lead.mca_company_info.date_of_balance_sheet}</strong> (MCA)
          </span>
        )}
        <input type="file" ref={aoc4Ref} accept=".pdf" className="hidden" onChange={e => handleAoc4Upload(e.target.files[0])} />
        {parsed.locked ? (
          <span className="h-7 text-[10px] px-2 py-1 rounded border border-slate-200 bg-slate-50 text-slate-500 flex items-center gap-1" title={parsed.locked_at ? `Locked by ${parsed.locked_by || 'Admin'} on ${new Date(parsed.locked_at).toLocaleString('en-IN')}` : 'AOC-4 data locked'} data-testid={`aoc4-locked-${lead.id}`}>
            <Lock className="w-3 h-3" /> AOC-4 Locked
            {parsed.locked_at && <span className="text-slate-400 font-normal ml-1 border-l border-slate-300 pl-1">{new Date(parsed.locked_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
          </span>
        ) : (
          <Button size="sm" variant="outline" onClick={() => aoc4Ref.current?.click()} disabled={uploading}
            className="h-7 text-xs gap-1 border-violet-300 text-violet-700 hover:bg-violet-50" data-testid={`upload-aoc4-${lead.id}`}>
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            Upload AOC-4 PDF
          </Button>
        )}
        {parsed.aoc4_source && !parsed.locked && (
          <Button size="sm" onClick={handleAoc4Lock} disabled={locking}
            className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`aoc4-lock-${lead.id}`}
            title="Lock AOC-4 parsed data after verifying values match the uploaded PDF. Re-upload will be blocked.">
            {locking ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Lock AOC-4
          </Button>
        )}
        {parsed.locked && (
          <Button size="sm" variant="outline" onClick={handleAoc4Unlock} disabled={locking}
            className="h-7 text-xs gap-1 border-slate-300 text-slate-600 hover:bg-slate-50" data-testid={`aoc4-unlock-${lead.id}`}
            title="Unlock AOC-4 to allow re-upload.">
            {locking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3" />}
            Unlock
          </Button>
        )}
        {parsed.aoc4_source && (
          <span className="text-[10px] text-slate-400">
            Parsed: {parsed.aoc4_source.company_name} | FY {parsed.aoc4_source.period_from} to {parsed.aoc4_source.period_to}{parsed.aoc4_source.period_to_prev ? ` (vs PY ${parsed.aoc4_source.period_to_prev})` : ''} | Net Worth: {parsed.aoc4_source.net_worth ? `₹${Number(parsed.aoc4_source.net_worth).toLocaleString('en-IN')}` : 'N/A'}
          </span>
        )}
      </div>
      )}
      {/* BS Analysis Lock Banner — visible when saved+locked */}
      {isLocked && !readOnly && (
        <div className="flex items-center gap-2 px-3 py-2 rounded border border-emerald-200 bg-emerald-50" data-testid={`bs-locked-banner-${lead.id}`}>
          <Lock className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
          <span className="text-[11px] text-emerald-800 font-medium">
            BS Analysis Locked
            {stored.locked_at && (
              <span className="ml-2 text-emerald-600 font-normal">
                · saved by {stored.locked_by || 'Admin'} on {new Date(stored.locked_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </span>
          <Button size="sm" variant="outline" onClick={handleBsUnlock} disabled={locking}
            className="ml-auto h-6 text-[10px] gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
            data-testid={`bs-unlock-${lead.id}`}>
            {locking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3" />}
            Unlock to Edit
          </Button>
        </div>
      )}
      {/* P&L Statement — Editable (AOC-4 format). Always shown so admin can enter P&L
          even when AOC-4 has no parsed P&L values (e.g., Section 8 / dormant companies). */}
      <fieldset disabled={fieldsReadOnly} className={`contents ${fieldsReadOnly ? 'opacity-90' : ''}`}>
        <div>
          <div className="bg-slate-100 rounded-t px-2.5 py-1.5 flex items-center justify-between">
            <p className="text-[9px] text-slate-700 uppercase tracking-wider font-bold">Statement of Profit & Loss</p>
            <p className="text-[9px] text-slate-500 italic normal-case">Totals auto-compute · hover a total to override (e.g., for tax)</p>
          </div>
          <div className="border border-t-0 border-slate-200 rounded-b">
            {PL_LINE_ITEMS.map(li => {
              const itemData = plItems[li.key] || {};
              const flag = itemData.discovery_flag || null;
              return (
                <div key={li.key} className={`grid grid-cols-12 gap-1 items-start px-2.5 py-1 border-t border-slate-50 hover:bg-slate-50/50 ${li.isTotal ? 'bg-slate-50' : ''} ${flagRowClass(flag)}`}>
                  <span className={`col-span-2 text-[10px] ${li.isTotal ? 'text-slate-800 font-bold' : li.isExpense ? 'text-slate-600 pl-3' : 'text-slate-700'} flex items-center gap-1`}>
                    {flag === 'red' && <Flag className="w-2.5 h-2.5 text-red-500 shrink-0" fill="currentColor" />}
                    {flag === 'amber' && <Flag className="w-2.5 h-2.5 text-amber-500 shrink-0" fill="currentColor" />}
                    {li.label}
                  </span>
                  {li.isTotal ? (
                    <div className="col-span-2 flex flex-col">
                      {itemData.manual_override ? (
                        <div className="flex items-center gap-1">
                          <input type="text" placeholder="₹" value={itemData.amount || ''} onChange={e => updatePlItem(li.key, 'amount', e.target.value)}
                            className={`flex-1 text-[10px] px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 tabular-nums text-right font-bold ${parseFloat(itemData.amount) < 0 ? 'text-red-600' : 'text-slate-800'}`}
                            data-testid={`pl-total-override-input-${li.key}`} />
                          <button type="button" onClick={() => updatePlItem(li.key, 'manual_override', false)}
                            className="text-[8px] text-amber-700 hover:text-amber-900 px-1 shrink-0"
                            title="Revert to auto-computed total"
                            data-testid={`pl-total-revert-${li.key}`}>↺</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 group">
                          <span className={`flex-1 text-[10px] font-bold tabular-nums text-right px-1.5 py-0.5 ${parseFloat(itemData.amount) < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                            {itemData.amount ? `₹${Number(itemData.amount).toLocaleString('en-IN')}` : '—'}
                          </span>
                          {!fieldsReadOnly && (
                            <button type="button" onClick={() => updatePlItem(li.key, 'manual_override', true)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-amber-600 shrink-0"
                              title="Override auto-computed value (e.g., to factor tax expense)"
                              data-testid={`pl-total-edit-${li.key}`}>
                              <Pencil className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </div>
                      )}
                      <YoYCell prevValue={parsedPlPrev[li.key]} currentValue={itemData.amount} />
                    </div>
                  ) : (
                    <div className="col-span-2 flex flex-col">
                      <input type="text" placeholder="₹" value={itemData.amount || ''} onChange={e => updatePlItem(li.key, 'amount', e.target.value)}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white tabular-nums text-right" />
                      <YoYCell prevValue={parsedPlPrev[li.key]} currentValue={itemData.amount} />
                    </div>
                  )}
                  <span className="col-span-2 text-[8px] text-violet-600 bg-violet-50 px-1 py-0.5 rounded border border-violet-100 leading-tight truncate" title={li.filings || itemData.filings_to_check || ''}>
                    {li.filings || itemData.filings_to_check || '—'}
                  </span>
                  {li.isTotal ? (
                    <>
                      <span className="col-span-2" />
                      <span className="col-span-1" />
                    </>
                  ) : (
                    <>
                      <select value={itemData.closure_path || ''} onChange={e => updatePlItem(li.key, 'closure_path', e.target.value)}
                        className="col-span-2 text-[10px] px-1 py-0.5 rounded border border-slate-200 bg-white">
                        <option value="">Path...</option>
                        {CLOSURE_PATHS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <select value={itemData.complexity || ''} onChange={e => updatePlItem(li.key, 'complexity', e.target.value)}
                        className="col-span-1 text-[10px] px-1 py-0.5 rounded border border-slate-200 bg-white">
                        <option value="">...</option>
                        {COMPLEXITY_LEVELS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </>
                  )}
                  <input type="text" placeholder="Notes" value={itemData.notes || ''} onChange={e => updatePlItem(li.key, 'notes', e.target.value)}
                    className={`col-span-2 text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white ${li.isTotal ? 'font-medium' : ''}`} />
                  <div className="col-span-1 flex justify-end items-center">
                    {!li.isTotal && !readOnly && (
                      <DiscoveryFlagToggle value={flag} onChange={v => updatePlItem(li.key, 'discovery_flag', v)} testId={`pl-flag-${li.key}`} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      {/* Balance Sheet — AOC-4 Schedule III format */}
      {(() => {
        const sections = [
          { key: 'equity_liab', title: 'EQUITY & LIABILITIES', totalKey: 'total_equity_liabilities' },
          { key: 'assets', title: 'ASSETS', totalKey: 'total_assets' },
        ];
        let lastGroup = '';
        const calcTotal = (sectionKey) => {
          return BS_LINE_ITEMS.filter(li => li.section === sectionKey).reduce((sum, li) => {
            const amt = parseFloat(items[li.key]?.amount) || 0;
            return sum + amt;
          }, 0);
        };

        return sections.map(sec => (
          <div key={sec.key}>
            <div className="bg-slate-100 rounded-t px-2.5 py-1.5">
              <p className="text-[9px] text-slate-700 uppercase tracking-wider font-bold">{sec.title}</p>
            </div>
            <div className="space-y-0 border border-t-0 border-slate-200 rounded-b">
              {BS_LINE_ITEMS.filter(li => li.section === sec.key).map((li) => {
                const itemData = items[li.key] || {};
                const showGroup = li.group && li.group !== lastGroup;
                const flag = itemData.discovery_flag || null;
                if (showGroup) lastGroup = li.group;
                return (
                  <div key={li.key}>
                    {showGroup && (
                      <div className="bg-slate-50 px-2.5 py-1 border-t border-slate-100">
                        <span className="text-[9px] text-slate-500 font-bold">{li.group}</span>
                      </div>
                    )}
                    <div className={`grid grid-cols-12 gap-1 items-start px-2.5 py-1 border-t border-slate-50 hover:bg-slate-50/50 ${flagRowClass(flag)}`}>
                      <span className={`col-span-2 text-[10px] text-slate-700 ${li.indent ? 'pl-3' : 'font-medium'} flex items-center gap-1`}>
                        {flag === 'red' && <Flag className="w-2.5 h-2.5 text-red-500 shrink-0" fill="currentColor" />}
                        {flag === 'amber' && <Flag className="w-2.5 h-2.5 text-amber-500 shrink-0" fill="currentColor" />}
                        {li.label}
                      </span>
                      <div className="col-span-2 flex flex-col">
                        <input type="text" placeholder="₹" value={itemData.amount || ''} onChange={e => updateItem(li.key, 'amount', e.target.value)}
                          className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white tabular-nums text-right" />
                        <YoYCell prevValue={parsedBsPrev[li.key]} currentValue={itemData.amount} />
                      </div>
                      <span className="col-span-2 text-[8px] text-violet-600 bg-violet-50 px-1 py-0.5 rounded border border-violet-100 leading-tight truncate" title={itemData.filings_to_check || ''}>
                        {itemData.filings_to_check || '—'}
                      </span>
                      <select value={itemData.closure_path || ''} onChange={e => updateItem(li.key, 'closure_path', e.target.value)}
                        className="col-span-2 text-[10px] px-1 py-0.5 rounded border border-slate-200 bg-white">
                        <option value="">Path...</option>
                        {CLOSURE_PATHS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <select value={itemData.complexity || ''} onChange={e => updateItem(li.key, 'complexity', e.target.value)}
                        className="col-span-1 text-[10px] px-1 py-0.5 rounded border border-slate-200 bg-white">
                        <option value="">...</option>
                        {COMPLEXITY_LEVELS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input type="text" placeholder="Notes" value={itemData.notes || ''} onChange={e => updateItem(li.key, 'notes', e.target.value)}
                        className="col-span-2 text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white" />
                      <div className="col-span-1 flex justify-end items-center">
                        {!readOnly && (
                          <DiscoveryFlagToggle value={flag} onChange={v => updateItem(li.key, 'discovery_flag', v)} testId={`bs-flag-${li.key}`} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {/* Total row */}
              <div className="grid grid-cols-12 gap-1 items-center px-2.5 py-1.5 border-t-2 border-slate-300 bg-slate-50">
                <span className="col-span-2 text-[10px] text-slate-800 font-bold">TOTAL</span>
                <div className="col-span-2 flex flex-col">
                  <span className="text-[10px] text-slate-800 font-bold tabular-nums text-right">
                    ₹{calcTotal(sec.key).toLocaleString('en-IN')}
                  </span>
                  {parsed.aoc4_source?.[`${sec.totalKey}_prev`] ? (
                    <YoYCell prevValue={parsed.aoc4_source[`${sec.totalKey}_prev`]} currentValue={parsed.aoc4_source[sec.totalKey]} />
                  ) : null}
                </div>
                {parsed.aoc4_source?.[sec.totalKey] ? (
                  <span className={`col-span-3 text-[9px] px-1.5 py-0.5 rounded ${
                    Math.abs(calcTotal(sec.key) - parsed.aoc4_source[sec.totalKey]) < 1
                      ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
                  }`}>
                    AOC-4: ₹{Number(parsed.aoc4_source[sec.totalKey]).toLocaleString('en-IN')}
                    {Math.abs(calcTotal(sec.key) - parsed.aoc4_source[sec.totalKey]) < 1 ? ' ✓' : ' ✕ mismatch'}
                  </span>
                ) : <span className="col-span-3" />}
              </div>
            </div>
          </div>
        ));
      })()}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Overall SoA Complexity</p>
          <select value={soaComplexity} onChange={e => { setSoaComplexity(e.target.value); setDirty(true); }} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white">
            <option value="">Select...</option>
            <option value="simple">Simple</option>
            <option value="moderate">Moderate</option>
            <option value="complex">Complex</option>
          </select>
        </div>
        <div>
          <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Summary Notes</p>
          <input type="text" placeholder="Overall BS observations..." value={overallNotes} onChange={e => { setOverallNotes(e.target.value); setDirty(true); }} className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 bg-white" />
        </div>
      </div>
      </fieldset>
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-3 text-[10px] text-slate-400">
          <span>{filledCount}/{BS_LINE_ITEMS.length} items analyzed</span>
          {(() => {
            const redCount = [...Object.values(items), ...Object.values(plItems)].filter(v => v?.discovery_flag === 'red').length;
            const amberCount = [...Object.values(items), ...Object.values(plItems)].filter(v => v?.discovery_flag === 'amber').length;
            if (redCount === 0 && amberCount === 0) return null;
            return (
              <span className="flex items-center gap-2" data-testid={`bs-flag-counts-${lead.id}`}>
                {redCount > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 border border-red-200 text-red-700 font-semibold">
                    <Flag className="w-2.5 h-2.5" fill="currentColor" /> {redCount} Red
                  </span>
                )}
                {amberCount > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 font-semibold">
                    <Flag className="w-2.5 h-2.5" fill="currentColor" /> {amberCount} Amber
                  </span>
                )}
                <span className="text-slate-400">flagged for Discovery Call</span>
              </span>
            );
          })()}
        </div>
        {!fieldsReadOnly && (
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className={`h-7 text-xs gap-1 transition-colors ${dirty ? 'bg-amber-500 hover:bg-amber-600 animate-pulse ring-2 ring-amber-300' : 'bg-[#1e3a5f] hover:bg-[#2a4f7a]'}`}
          data-testid={`save-bs-${lead.id}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          {dirty ? 'Save Changes' : 'Save Analysis'}
        </Button>
        )}
      </div>
    </div>
  );
}
